import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import { app_modules } from "helpers/apps_modules_list";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";
import { downloadOrderPdf, exportOrderCSV, fetchOrder } from "slices/Inventory/Order/thunk";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";

import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import ActionButton from "components/Buttons/ActionButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import OrderStatusBadge from "components/StatusBadge/OrderStatusBadge";

const Order = () => {
  document.title = "Order List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  const selectPRs = createSelector(
    (state: any) => state.InventoryOrderReducer,
    (data) => ({
      list: data.list,
      rowCount: data.rowCount,
      loading: data.loading,
      error: data.error,
    })
  );

  const { list, rowCount, loading, error } = useSelector(selectPRs);

  // Use custom hook to fetch and access permissions
  const { canView, canUpdate } = usePermission(
    app_modules?.inventory?.app_name,
    app_modules?.inventory?.modules?.order
  );

  // const order_app_permissions = user_permissions?.find(
  //   (item: any) => item?.apps_name === app_modules?.order?.app_name
  // );
  // const order_permissions = order_app_permissions?.modules_permissions?.find(
  //   (item: any) => item?.modules_name === app_modules?.order?.modules?.order
  // );

  // 🔥 Use preserved table state hook
  const tableState = usePreservedTableState({
    key: "order",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const [data, setData] = useState([]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);
  useEffect(() => {
    dispatch(
      fetchOrder({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);

  useEffect(() => {
    setData(list);
  }, [list]);

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await exportOrderCSV({
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      });

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export order data";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const handleDownloadPDF = useCallback(
    async (order_id: string, order_no?: string) => {
      if (!order_id) {
        toast.error("Order ID not found");
        return;
      }

      showCustomSwal({
        title: "Downloading PDF...",
        text: "Please wait while we prepare your order invoice.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const { blob, filename } = await dispatch(
          downloadOrderPdf({ id: order_id, orderNumber: order_no })
        ).unwrap();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        Swal.close();
        toast.success("PDF downloaded");
      } catch (error: any) {
        console.error("Order PDF download error:", error);
        const message =
          error?.message ||
          error?.response?.data?.message ||
          "Failed to download order PDF";
        Swal.close();
        showCustomSwal({
          icon: "error",
          title: "Download Failed",
          text: message,
        });
      }
    },
    [dispatch]
  );

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      // {
      //   icon: EditOutlinedIcon,
      //   label: "Edit",
      //   permission: (row: any) => {
      //     return (
      //       order_permissions?.permissions?.Update === true &&
      //       row?.order_status?.toLowerCase() !== "cancelled" &&
      //       row?.order_status?.toLowerCase() !== "partially cancelled"
      //     );
      //   },
      //   onClick: (row: any) => {
      //     navigate(`/edit-order/${row.order_id}`, {
      //       state: row,
      //     });
      //   },
      //   color: "primary",
      // },
      {
        icon: VisibilityOutlinedIcon,
        label: "View Details",
        permission: canView,
        link: (row: any) => `/view-inventoryorder/${row.order_id}`,
        color: "info",
      },
      {
        icon: PictureAsPdfIcon,
        label: "Download PDF",
        permission: canView,
        onClick: (row: any) => handleDownloadPDF(row.order_id, row.order_no),
        color: "themeColor",
      },
      // {
      //   icon: LocalShippingOutlinedIcon,
      //   label: "Request for Dispatch",
      //   permission: order_permissions?.permissions?.View === true,
      //   onClick: (row: any) => {
      //     if (row?.order_status?.toLowerCase() !== "cancelled") {
      //       handleRequestDispatch(row.order_id);
      //     }
      //   },
      //   color: "info",
      // },
      // {
      //   icon: CloseOutlinedIcon,
      //   label: "Cancel Order",
      //   permission: (row: any) => {
      //     return (
      //       order_permissions?.permissions?.View === true &&
      //       row?.order_status?.toLowerCase() !== "cancelled"
      //     );
      //   },
      //   onClick: (row: any) => {
      //     handleCancelOrder(row.order_id);
      //   },
      //   color: "error",
      // },
    ];
    return items;
  }, [canView, handleDownloadPDF]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate || canView;

    if (!hasPermission) return [];

    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [canUpdate, canView, menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Order No.",
        accessorKey: "order_no",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "inventory/order/dropdown/order_no",
        cell: ({ row }: { row: any }) => {
          return row?.original?.order_no || "-";
        },
      },
      {
        header: "Quotation No.",
        accessorKey: "quotation_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "inventory/order/dropdown/quotation_number",
        cell: ({ row }: { row: any }) => {
          return row?.original?.quotation?.quotation_number || "-";
        },
      },
      {
        header: "Order Date",
        accessorKey: "order_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = row.original.order_date
            ? new Date(row.original.order_date).toISOString().split("T")[0]
            : "N/A";
          return `${date}`;
        },
      },

      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "inventory/order/dropdown/customer_name",
        cell: ({ row }: { row: any }) => {
          return row?.original?.customer?.customer_name || "-";
        },
      },
      {
        header: "Dispatch from warehouse",
        accessorKey: "warehouse_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "inventory/order/dropdown/warehouse_name",
        cell: ({ row }: { row: any }) => {
          return row?.original?.warehouse?.warehouse_name || "-";
        },
      },
      {
        header: "Order Status",
        accessorKey: "order_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "inventory/order/dropdown/order_status",
        cell: ({ row }: { row: any }) => {
          return (
            <OrderStatusBadge status={row?.original?.order_status || "-"} />
          );
        },
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },

      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },
      ...DynamicAction,
    ],
    [DynamicAction]
  );

  return (
    <ListingPageContainer>
      <Card>
        <Card.Header>
          <Row className="align-items-center g-2">
            <Col lg={3} className="me-auto">
              <h6 className="card-title mb-0">Order</h6>
            </Col>
            {/* Search Input Box (reusable)*/}
            <Col xs={12} sm={12} md={6} lg={3} xl={3}>
              <SearchBox value={searchInput} onChange={setSearchInput} />
            </Col>

            <Col className="col-md-auto">
              <div className="hstack gap-2">
                {canView && (
                  <ActionButton
                    label="Export"
                    icon="bi bi-box-arrow-up"
                    handleClick={handleExport}
                    variant="light"
                  />
                )}

                {/* {order_permissions?.permissions?.Create === true && (
                  <ActionButton
                    label="Create Order"
                    icon="bi bi-plus-circle"
                    navigateLink="/add-order"
                    variant="primary"
                  />
                )} */}
              </div>
            </Col>

            <Col className="col-md-auto"></Col>
          </Row>
        </Card.Header>
        <Card.Body className="mt-3">
          <LoaderErrorWrapper loading={loading} error={error}>
            <TableContainer2
              columns={columns}
              data={data}
              totalRecords={rowCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setFilterValue}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              isGlobalFilter={false}
              SearchPlaceholder="Search ..."
              manualSorting={true}
            />
          </LoaderErrorWrapper>
        </Card.Body>
      </Card>
    </ListingPageContainer>
  );
};

export default Order;
